import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { authFetch } from "../../utils/authFetch";
import Swal from "sweetalert2";
import "./MoviePlayer.css";

const MoviePlayer = () => {
  const { id } = useParams();
  const [movie, setMovie] = useState(null);
  const [comments, setComments] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [newRating, setNewRating] = useState({ score: 0, review: "" });
  const [favorite, setFavorite] = useState(false);
  const [favoriteCount, setFavoriteCount] = useState(0);

  const user = JSON.parse(localStorage.getItem('user')) || {};
  const userId = user.userId || null;

  const fetchMovie = useCallback(async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${baseUrl}/movies/${id}`);
      if (!response.ok) throw new Error("Lỗi khi lấy dữ liệu phim");
      const data = await response.json();
      setMovie(data);
    } catch (error) {
      console.error("Lỗi khi tải phim:", error);
      setMovie(null);
    }
  }, [id]);

  const fetchComments = useCallback(async () => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const res = await fetch(`${baseUrl}/comments/movie/${id}?page=0&size=5`);
    const data = await res.json();
    setComments(data.content || []);
  }, [id]);

  const fetchRatings = useCallback(async () => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const res = await fetch(`${baseUrl}/ratings/movie/${id}`);
    const data = await res.json();
    setRatings(data);
  }, [id]);

  const fetchFavorite = useCallback(async () => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const res = await fetch(`${baseUrl}/favorites?movieId=${id}&page=0&size=1000`);
    const data = await res.json();
    const favoriteList = data.content || [];
    setFavoriteCount(favoriteList.length);
    setFavorite(favoriteList.some(fav => fav.userId === userId));
  }, [id, userId]);

  const fetchAllData = useCallback(async () => {
    await fetchMovie();
    await fetchComments();
    await fetchRatings();
    await fetchFavorite();
  }, [fetchMovie, fetchComments, fetchRatings, fetchFavorite]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleCommentSubmit = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const res = await authFetch(`${baseUrl}/comments?userId=${userId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ movieId: parseInt(id), content: newComment }),
      });
      if (!res.ok) throw new Error("Không thể gửi bình luận");
      setNewComment("");
      Swal.fire("Thành công", "Đã gửi bình luận!", "success").then(() => fetchAllData());
    } catch (err) {
      Swal.fire("Lỗi", err.message, "error");
    }
  };

  const handleRatingSubmit = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const checkRes = await authFetch(`${baseUrl}/ratings/movie/${id}`);
      if (!checkRes.ok) throw new Error("Không thể kiểm tra đánh giá");
      const existingRatings = await checkRes.json();
      const existingRating = existingRatings.find(r => r.userId === userId);
      const url = existingRating
        ? `${baseUrl}/ratings/${userId}/${id}` // PUT
        : `${baseUrl}/ratings?userId=${userId}`; // POST

      const method = existingRating ? "PUT" : "POST";

      const res = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          movieId: parseInt(id),
          score: parseFloat(newRating.score),
          review: newRating.review,
        }),
      });

      if (!res.ok) throw new Error("Không thể gửi đánh giá");

      setNewRating({ score: 0, review: "" });
      Swal.fire("Thành công", existingRating ? "Đã cập nhật đánh giá!" : "Đã gửi đánh giá!", "success")
        .then(() => fetchAllData());
    } catch (err) {
      Swal.fire("Lỗi", err.message, "error");
    }
  };

  const toggleFavorite = async () => {
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL;
      const endpoint = favorite
        ? `${baseUrl}/favorites?userId=${userId}&movieId=${id}`
        : `${baseUrl}/favorites?userId=${userId}`;

      const res = await authFetch(endpoint, {
        method: favorite ? "DELETE" : "POST",
        headers: { "Content-Type": "application/json" },
        body: favorite ? undefined : JSON.stringify({ movieId: parseInt(id) }),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || "Không thể cập nhật yêu thích");
      }

      setFavorite(!favorite);
      await fetchFavorite();
    } catch (err) {
      console.error("Favorite error:", err);
      Swal.fire("Lỗi", err.message, "error");
    }
  };

  const renderStars = (score = null) => {
    const starsToRender = score || newRating.score;
    return Array.from({ length: 5 }, (_, index) => {
      const starIndex = index + 1;
      return (
        <span
          key={index}
          className={`star ${starIndex <= starsToRender ? "filled" : ""}`}
          onClick={score === null ? () => setNewRating({ ...newRating, score: starIndex }) : null}
        >
          ★
        </span>
      );
    });
  };

  if (!movie) return (
    <div className="loading-screen">
      <div className="spinner"></div>
      <p>Đang tải phim...</p>
    </div>
  );

  return (
    <div className="cinema-container">
      {/* Curtains effect */}
      <div className="curtain-left"></div>
      <div className="curtain-right"></div>

      {/* Screen effect */}
      <div className="screen-effect">
        <div className="screen-glow"></div>
      </div>

      <div className="movie-theater">
        <div className="theater-screen">
          <div className="video-wrapper">
            <video controls className="movie-video" preload="metadata">
              <source src={movie.videoUrl} type="video/mp4" />
              Trình duyệt của bạn không hỗ trợ video.
            </video>
          </div>
        </div>

        <div className="movie-details">
          <div className="infor">
            <div className="movie-header">
              <h1 className="movie-title">{movie.title}</h1>
              <div className="movie-meta">
                <span className="meta-item">{new Date(movie.releaseDate).getFullYear()}</span>
                <span className="meta-item">{movie.duration} phút</span>
                <span className="meta-item">{movie.genres.join(", ")}</span>
              </div>

              <div className="movie-actions">
                <button onClick={toggleFavorite} className={`favorite-btn ${favorite ? 'active' : ''}`}>
                  {favorite ? "❤️ Đã thích" : "🤍 Thích"} | {favoriteCount}
                </button>
              </div>
            </div>

            <div className="movie-info">
              <h3 className="section-title">Nội dung</h3>
              <p className="movie-description">{movie.description}</p>

              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Đạo diễn:</span>
                  <span className="info-value">{movie.director}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Ngày phát hành:</span>
                  <span className="info-value">{new Date(movie.releaseDate).toLocaleDateString()}</span>
                </div>
              </div>
            </div>

            <div className="comment-section">
              <h3 className="section-title">Bình luận</h3>
              <div className="comment-input">
                <textarea
                  placeholder="Viết bình luận của bạn..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="comment-textarea"
                ></textarea>
                <button onClick={handleCommentSubmit} className="submit-btn">Gửi bình luận</button>
              </div>

              <div className="comments-scroll-container">
                <div className="comments-list">
                  {comments.length > 0 ? (
                    comments.map((c) => (
                      <div key={c.id} className="comment-item">
                        <div className="comment-header">
                          <span className="comment-user">{c.username || "Khách"}</span>
                          <span className="comment-time">
                            {new Date(c.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="comment-content">{c.content}</p>
                      </div>
                    ))
                  ) : (
                    <p className="no-comments">Chưa có bình luận nào</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="rate">
            <div className="rating-section">
              <h3 className="section-title">Đánh giá của bạn</h3>
              <div className="rating-input">
                <div className="rating-stars-input">
                  {renderStars()}
                  <span className="rating-score">{newRating.score}/5</span>
                </div>
                <textarea
                  placeholder="Viết đánh giá chi tiết..."
                  value={newRating.review}
                  onChange={(e) => setNewRating({ ...newRating, review: e.target.value })}
                  className="review-textarea"
                ></textarea>
                <button onClick={handleRatingSubmit} className="submit-btn">Gửi đánh giá</button>
              </div>

              <h3 className="section-title">Đánh giá từ khán giả</h3>
              <div className="ratings-scroll-container">
                <div className="ratings-list">
                  {ratings.length > 0 ? (
                    ratings.map((r) => (
                      <div key={r.id} className="rating-item">
                        <div className="rating-header">
                          <span className="rating-user">{r.username}</span>
                          <div className="rating-stars-display">
                            {renderStars(r.score)}
                          </div>
                        </div>
                        <p className="rating-review">{r.review}</p>
                        <div className="rating-date">
                          {new Date(r.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="no-ratings">Chưa có đánh giá nào</p>
                  )}
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default MoviePlayer;